/**
 * @file Движок фильтрации каталога.
 * Компилирует активные фильтры в предикаты и применяет их к массиву элементов.
 */

import { getFilterDefinition } from './filter_definitions'

/**
 * Проверяет, является ли значение фильтра пустым (null, undefined, пустая строка или пустой массив).
 * @param {*} value
 * @returns {boolean}
 */
function isEmptyFilterValue(value) {
  if (value === null || typeof value === 'undefined' || value === '') {
    return true
  }

  return Array.isArray(value) && value.length === 0
}

/**
 * Компилирует объект активных фильтров в массив функций-предикатов.
 * Каждый предикат принимает элемент каталога и возвращает true, если элемент проходит фильтр.
 * @param {Object.<string, *>} filters - Активные фильтры: имя фильтра → значения.
 * @param {object} [context={}] - Контекст выполнения (например, `{ now: Date }` для фильтра по времени).
 * @returns {Array<function(object): boolean>}
 */
function compileFilters(filters, context = {}) {
  const filterContext = { now: new Date(), ...context }

  return Object.entries(filters).flatMap(([filterName, rawFilterValue]) => {
    const definition = getFilterDefinition(filterName)

    const isEmpty = definition.isEmpty || isEmptyFilterValue
    if (isEmpty(rawFilterValue)) return []

    const filterValue = definition.normalizeFilterValue(rawFilterValue)

    return [
      (item) => {
        const itemValue = definition.getItemValue
          ? definition.getItemValue(item, filterName)
          : item[filterName]

        return definition.matches(itemValue, filterValue, filterContext)
      }
    ]
  })
}

/**
 * Фильтрует массив элементов каталога по заданным фильтрам.
 * Возвращает только те элементы, которые удовлетворяют всем активным фильтрам.
 * @param {object[]} items - Элементы каталога.
 * @param {Object.<string, *>} filters - Активные фильтры.
 * @param {object} [context={}] - Контекст выполнения.
 * @returns {object[]}
 */
function filterItems(items, filters, context = {}) {
  const predicates = compileFilters(filters, context)

  if (predicates.length === 0) return [...items]

  return items.filter((item) => {
    return predicates.every((predicate) => predicate(item))
  })
}

export { compileFilters, filterItems }
